import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Alert, Button, Container, FormField, Input, SpaceBetween } from '@amzn/awsui-components-react';

import DrishtiPageHeader from '../../components/drishtiPageHeader';
import { useDrishtiPool } from '../../config/drishtiPoolConfig';
import { confirmEmailOtp, startEmailOtpSignIn } from '../../services/drishtiAuthService';
import type { DrishtiAuthErrorCode } from '../../services/drishtiAuthTypes';
import { toDrishtiUserSession } from '../../services/drishtiSession';
import { EventName, EventType, logDrishtiMetricEvent } from '../../services/eventService';
import { clearDrishti } from '../../slice/drishtiInstallConfigSlice';
import { updateDrishtiSession } from '../../slice/drishtiUserInfoSlice';

type LoginStep = 'email' | 'code';

const STEP_ONE_LABEL = 'Step 1: Enter your email';
const STEP_TWO_LABEL = 'Step 2: Enter the code we emailed you';
const RESEND_LABEL = 'Resend code';

const EMAIL_STEP_ERROR_COPY: Record<DrishtiAuthErrorCode, string> = {
    CODE_MISMATCH: 'We could not send a code. Try again.',
    CODE_EXPIRED: 'We could not send a code. Try again.',
    ATTEMPT_LIMIT_EXCEEDED: 'Too many code requests. Wait a few minutes, then try again.',
    SESSION_EXPIRED: 'That sign-in attempt timed out. Send a new code.',
    NETWORK: 'No connection. Check your network, then send the code again.',
    UNKNOWN: 'We could not send a code. Try again.',
};

const CODE_STEP_ERROR_COPY: Record<DrishtiAuthErrorCode, string> = {
    CODE_MISMATCH: 'That code is not right. Check the email and enter the code again.',
    CODE_EXPIRED: `That code has expired. Choose ${RESEND_LABEL} to get a new one.`,
    ATTEMPT_LIMIT_EXCEEDED: `Too many wrong codes. Wait a few minutes, then choose ${RESEND_LABEL}.`,
    SESSION_EXPIRED: `This sign-in attempt timed out. Choose ${RESEND_LABEL} to get a new one.`,
    NETWORK: 'No connection. Check your network, then enter the code again.',
    UNKNOWN: `We could not check that code. Try again, or choose ${RESEND_LABEL}.`,
};

const DrishtiLogin = () => {
    const poolParams = useDrishtiPool();
    const dispatch = useDispatch();

    const [step, setStep] = useState<LoginStep>('email');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [session, setSession] = useState<string | null>(null);
    const [isSending, setIsSending] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [emailStepError, setEmailStepError] = useState('');
    const [codeStepError, setCodeStepError] = useState('');
    const [resendError, setResendError] = useState('');

    const requestCode = async (isResend: boolean) => {
        if (!poolParams) {
            return;
        }
        const setRequestError = isResend ? setResendError : setEmailStepError;
        setRequestError('');
        setIsSending(true);
        const result = await startEmailOtpSignIn({ ...poolParams, email });
        setIsSending(false);
        if (!result.ok) {
            const copy = EMAIL_STEP_ERROR_COPY[result.code];
            setRequestError(isResend ? `${RESEND_LABEL}: ${copy}` : copy);
            return;
        }
        setSession(result.value.session);
        setCode('');
        setCodeStepError('');
        setStep('code');
    };

    const verifyCode = async () => {
        if (!poolParams || !session) {
            return;
        }
        setCodeStepError('');
        setIsVerifying(true);
        const verifyStartTime = Date.now();
        const result = await confirmEmailOtp({ ...poolParams, email, code, session });
        setIsVerifying(false);
        if (!result.ok) {
            setCodeStepError(CODE_STEP_ERROR_COPY[result.code]);
            if (result.code === 'SESSION_EXPIRED') {
                // The session is spent, so block further attempts until a resend replaces it.
                setSession(null);
            }
            return;
        }
        // A new sign-in starts a fresh install; the device may have changed hands.
        dispatch(clearDrishti());
        // The route guard redirects on this slice, so this page never navigates itself.
        dispatch(updateDrishtiSession(toDrishtiUserSession(result.value)));
        // Logged after the session lands, because this is the first event with a token to send it with.
        logDrishtiMetricEvent({
            eventName: EventName.DRISHTI_LOGIN_ATTEMPT,
            eventType: EventType.SUCCESS,
            duration_ms: Date.now() - verifyStartTime,
        });
    };

    const changeEmail = () => {
        setStep('email');
        setSession(null);
        setCode('');
        setCodeStepError('');
        setResendError('');
    };

    if (!poolParams) {
        return (
            <Container>
                <DrishtiPageHeader title="Sign in" />
                <Alert type="error" header="Sign-in configuration unavailable">
                    This app is missing its Drishti sign-in settings, so we cannot email you a code. Refresh the page,
                    and tell your manager if it keeps happening.
                </Alert>
            </Container>
        );
    }

    return (
        <Container>
            <DrishtiPageHeader title="Sign in" />
            {step === 'email' ? (
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        void requestCode(false);
                    }}
                >
                    <SpaceBetween size="l">
                        <FormField label={STEP_ONE_LABEL} errorText={emailStepError}>
                            <Input
                                type="email"
                                inputMode="email"
                                autoComplete="username"
                                value={email}
                                onChange={({ detail }) => {
                                    setEmail(detail.value.trim().toLowerCase());
                                    setEmailStepError('');
                                }}
                            />
                        </FormField>
                        <Button variant="primary" loading={isSending} disabled={!email}>
                            Email me a code
                        </Button>
                    </SpaceBetween>
                </form>
            ) : (
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        void verifyCode();
                    }}
                >
                    <SpaceBetween size="l">
                        <FormField
                            label={STEP_TWO_LABEL}
                            description={`We emailed a code to ${email}. It expires after a few minutes.`}
                            errorText={codeStepError}
                        >
                            <Input
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                value={code}
                                onChange={({ detail }) => {
                                    setCode(detail.value.trim());
                                    setCodeStepError('');
                                }}
                            />
                        </FormField>
                        <Button variant="primary" loading={isVerifying} disabled={!code || !session}>
                            Verify and sign in
                        </Button>
                        <SpaceBetween size="xs">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button formAction="none" loading={isSending} onClick={() => void requestCode(true)}>
                                    {RESEND_LABEL}
                                </Button>
                                <Button formAction="none" onClick={changeEmail}>
                                    Use a different email
                                </Button>
                            </SpaceBetween>
                            {resendError ? <Alert type="error">{resendError}</Alert> : null}
                        </SpaceBetween>
                    </SpaceBetween>
                </form>
            )}
        </Container>
    );
};

export default DrishtiLogin;
